import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'cart_reservation_end';

class CartReservationCountdown extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
    this.timeSpan = null;
  }

  connectedCallback() {
    this.timeSpan = this.querySelector('[data-ref="reservation-time"]');
    if (!this.timeSpan) return;

    document.addEventListener(ThemeEvents.cartUpdated, this.#onCartUpdated);

    const initialItemCount = parseInt(this.getAttribute('data-initial-item-count'), 10);
    if (Number.isFinite(initialItemCount) && initialItemCount === 0) {
      this.#resetForEmptyCart();
      return;
    }

    this.#startTimer();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.#onCartUpdated);
    this.#clearIntervalSafe();
  }

  #onCartUpdated = async (event) => {
    if (!this.timeSpan) return;

    const count = event.detail?.data?.itemCount;
    if (count === 0) {
      this.#resetForEmptyCart();
      return;
    }

    if (typeof count === 'number' && count > 0 && !Number.isNaN(count)) {
      this.#ensureTimerRunning();
      return;
    }

    // Pas de cart-icon / itemCount non calculé (ex. product-form) : déduire l’état via cart.js
    if (this.interval) return;

    await this.#syncFromCartJson();
  };

  #syncFromCartJson = async () => {
    if (!this.timeSpan || this.interval) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = Number(cart.item_count);

      if (itemCount === 0) {
        this.#resetForEmptyCart();
        return;
      }

      if (itemCount > 0) {
        this.#ensureTimerRunning();
      }
    } catch {
      // Ignorer erreurs réseau / parse
    }
  };

  #durationMinutes() {
    return parseInt(this.getAttribute('data-duration-minutes'), 10) || 15;
  }

  #resetForEmptyCart() {
    sessionStorage.removeItem(STORAGE_KEY);
    this.#clearIntervalSafe();
    this.timeSpan.textContent = `${this.#durationMinutes()}:00`;
  }

  #clearIntervalSafe() {
    if (!this.interval) return;

    clearInterval(this.interval);
    this.interval = null;
  }

  #startTimer() {
    const durationMinutes = this.#durationMinutes();
    let endTime = parseInt(sessionStorage.getItem(STORAGE_KEY), 10);

    if (!endTime || endTime <= Date.now()) {
      endTime = Date.now() + durationMinutes * 60 * 1000;
      sessionStorage.setItem(STORAGE_KEY, String(endTime));
    }

    this.#updateDisplay(endTime);
    this.#clearIntervalSafe();
    this.interval = setInterval(() => this.#updateDisplay(endTime), 1000);
  }

  #ensureTimerRunning() {
    if (!this.timeSpan || this.interval) return;

    this.#startTimer();
  }

  #updateDisplay(endTime) {
    const now = Date.now();
    const remainingMs = endTime - now;

    if (remainingMs <= 0) {
      sessionStorage.removeItem(STORAGE_KEY);
      this.#clearIntervalSafe();
      this.timeSpan.textContent = '0:00';
      this.dispatchEvent(new CustomEvent('cart-reservation-ended', { bubbles: true }));
      return;
    }

    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.timeSpan.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

if (!customElements.get('cart-reservation-countdown')) {
  customElements.define('cart-reservation-countdown', CartReservationCountdown);
}
